import axios, { type AxiosInstance, type InternalAxiosRequestConfig, type AxiosResponse, type AxiosError } from 'axios'

/**
 * Read a cookie value by name from document.cookie.
 */
function getCookie(name: string): string | null {
  const cookies = document.cookie.split(';')
  for (const cookie of cookies) {
    const [key, ...rest] = cookie.split('=')
    if (key.trim() === name) {
      return decodeURIComponent(rest.join('=').trim())
    }
  }
  return null
}

const MUTATING_METHODS = ['post', 'put', 'patch', 'delete']

const client: AxiosInstance = axios.create({
  baseURL: '/api',
  withCredentials: true,
  timeout: 30000,
  headers: {
    'Content-Type': 'application/json',
  },
})

// Attach CSRF token on mutating requests
client.interceptors.request.use((config: InternalAxiosRequestConfig) => {
  const method = (config.method ?? '').toLowerCase()
  if (MUTATING_METHODS.includes(method)) {
    const token = getCookie('csrftoken') ?? getCookie('csrf_access_token')
    if (token) {
      config.headers.set('X-CSRF-Token', token)
    }
  }
  return config
})

let isRefreshing = false
let failedQueue: Array<{
  resolve: (value: AxiosResponse) => void
  reject: (reason: unknown) => void
  config: InternalAxiosRequestConfig
}> = []

function processQueue(error: unknown) {
  failedQueue.forEach(({ reject }) => reject(error))
  failedQueue = []
}

client.interceptors.response.use(
  (response: AxiosResponse) => response,
  async (error: AxiosError) => {
    const originalRequest = error.config as InternalAxiosRequestConfig & { _retry?: boolean }

    if (error.response?.status === 401 && !originalRequest._retry) {
      if (isRefreshing) {
        return new Promise<AxiosResponse>((resolve, reject) => {
          failedQueue.push({ resolve, reject, config: originalRequest })
        })
      }

      originalRequest._retry = true
      isRefreshing = true

      try {
        await axios.post('/api/auth/refresh', {}, { withCredentials: true })
        isRefreshing = false
        // Retry queued requests that failed during refresh
        const retries = failedQueue.map(({ config, resolve, reject }) =>
          client.request(config).then(resolve).catch(reject)
        )
        failedQueue = []
        await Promise.allSettled(retries)
        // Retry the original request that triggered the refresh
        return client.request(originalRequest)
      } catch (refreshError) {
        isRefreshing = false
        processQueue(refreshError)
        window.location.href = '/login'
        return Promise.reject(refreshError)
      }
    }

    return Promise.reject(error)
  },
)

export default client

// Extended timeout client for long-running operations (simulation, NDVI fetch)
export const longPollClient: AxiosInstance = axios.create({
  baseURL: '/api',
  withCredentials: true,
  timeout: 180000,
  headers: {
    'Content-Type': 'application/json',
  },
})
